import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import StorageService from "../../services/StorageService";
import VehicleTypeService from "../../services/VehicleTypeService";
import LeaseTypeService from "../../services/LeaseTypeService";
import VehicleHireReportService from "../../services/vehicleHireReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import logo from '../../assets/images/deviops_logo.png';
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";
import MessageDialog from "../../components/MessageDialog";


export default function VehicleHireReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";
  const [vehicleTypeId, setVehicleTypeId] = useState("");
  const [leaseTypeId, setLeaseTypeId] = useState("");
  const [fuelBorneBy, setFuelBorneBy] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [leaseTypes, setLeaseTypes] = useState([]);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");

  // ✅ PAGINATION
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonthDateRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: formatInputDate(startDate),
      endDate: formatInputDate(endDate),
    };
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    setTimeout(() => setMessage(""), 3000);
  };

  const loadDropdowns = async () => {
    try {
      const [vehicleTypeRes, leaseTypeRes] = await Promise.all([
        VehicleTypeService.getActiveVehicleTypes(),
        LeaseTypeService.getActive(),
      ]);

      setVehicleTypes(vehicleTypeRes?.data || vehicleTypeRes || []);
      setLeaseTypes(leaseTypeRes?.data || leaseTypeRes || []);
    } catch (error) {
      console.error(error);
      setVehicleTypes([]);
      setLeaseTypes([]);
      showMessage("Failed to load filter dropdowns");
    }
  };

  const loadReport = async (pageNumber = page) => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      showMessage("Start Date should be less than or equal to End Date");
      return;
    }

    setLoading(true);
    try {
      const res = await VehicleHireReportService.getReport({
        companyId: companyId || undefined,
        vehicleTypeId: vehicleTypeId || undefined,
        leaseTypeId: leaseTypeId || undefined,
        fuelBorneBy: fuelBorneBy || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,

        // ✅ PAGINATION PARAMS
        page: pageNumber,
        limit: limit,
      });

      // ✅ IMPORTANT
      setReports(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);

    } catch (error) {
      console.error(error);
      setReports([]);
      showMessage("Error fetching vehicle hire report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
    setStartDate(monthStart);
    setEndDate(monthEnd);
    loadReport(1);
  }, []);

  const handleClearFilters = () => {
    setVehicleTypeId("");
    setLeaseTypeId("");
    setFuelBorneBy("");
    const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
    setStartDate(monthStart);
    setEndDate(monthEnd);

    // ✅ RESET PAGINATION
    setPage(1);
    setTotalPages(0);

    loadReport(1);
  };

  const getAllReportData = async () => {
    try {
      let allData = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const res = await VehicleHireReportService.getReport({
          companyId: companyId || undefined,
          vehicleTypeId: vehicleTypeId || undefined,
          leaseTypeId: leaseTypeId || undefined,
          fuelBorneBy: fuelBorneBy || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: currentPage,
          limit: 5   // same as your UI
        });

        const data = res?.data || [];
        const pagination = res?.pagination || {};

        allData = [...allData, ...data];

        totalPages = pagination.totalPages || 1;
        currentPage++;

      } while (currentPage <= totalPages);

      return allData;

    } catch (error) {
      console.error("PDF error:", error);
      return [];
    }
  };

  const exportExcel = async () => {
    const allData = await getAllReportData();

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }
    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const sortedData = [...allData].sort((a, b) => {
      const dateA = new Date(a.startDate || 0).getTime();
      const dateB = new Date(b.startDate || 0).getTime();
      return dateA - dateB;
    });

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];

    // Get vehicle type label - use first row data if available
    let vehicleTypeLabel = "All";
    if (vehicleTypeId) {
      const foundType = vehicleTypes.find(v => String(v.id) === String(vehicleTypeId));
      vehicleTypeLabel = foundType?.vehicleType || (allData[0]?.vehicleType || vehicleTypeId);
    }

    // Get lease type label - use first row data if available
    let leaseTypeLabel = "All";
    if (leaseTypeId) {
      const foundType = leaseTypes.find(l => String(l.id) === String(leaseTypeId));
      leaseTypeLabel = foundType?.leaseType || (allData[0]?.leaseType || leaseTypeId);
    }

    // Get fuel borne by label - use first row data if available
    let fuelBorneByLabel = "All";
    if (fuelBorneBy) {
      fuelBorneByLabel = fuelBorneBy;
    }

    let row = 0;

    // ✅ STYLES
    const thin = { style: "thin" };
    const center = {
      horizontal: "center",
      vertical: "center",
      wrapText: true
    };

    // ========================
    // COMPANY NAME (LEFT)
    // ========================
    XLSX.utils.sheet_add_aoa(ws, [[activeCompany?.companyName || ""]], {
      origin: { r: row, c: 0 }
    });

    // ========================
    // REPORT DATE (RIGHT)
    // ========================
    XLSX.utils.sheet_add_aoa(ws, [[`Report Date: ${utility.formatExportDateTime(new Date())}`]], {
      origin: { r: row, c: 8 }
    });

    // STYLE (optional)
    const companyCell = XLSX.utils.encode_cell({ r: row, c: 0 });
    ws[companyCell].s = { font: { bold: true, sz: 12 } };

    const dateCell = XLSX.utils.encode_cell({ r: row, c: 8 });
    ws[dateCell].s = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "right" }
    };

    row += 2;

    // ========================
    // TITLE
    // ========================
    XLSX.utils.sheet_add_aoa(ws, [["Vehicle Hire Report"]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 9 } });
    const titleCell = XLSX.utils.encode_cell({ r: row, c: 0 });
    ws[titleCell].s = {
      font: { bold: true, sz: 16 },
      alignment: center,
      border: {
        top: thin,
        bottom: thin,
        left: thin,
        right: thin
      }
    };
    row += 2;

    // ========================
    // FILTER INFO
    // ========================
    XLSX.utils.sheet_add_aoa(ws, [
      ["Vehicle Type:", vehicleTypeLabel, "", "Start Date:", startDate ? utility.formatDateDDMMMYYYY(startDate) : "-"],
      ["Lease Type:", leaseTypeLabel, "", "End Date:", endDate ? utility.formatDateDDMMMYYYY(endDate) : "-"],
      ["Fuel Borne By:", fuelBorneByLabel, "", ""]
    ], { origin: { r: row, c: 0 } });

    const filterLabelStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin }
    };

    const filterValueStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin }
    };

    const boldLabelColumns = [0, 3];
    for (let i = 0; i < 3; i++) {
      for (let c = 0; c < 5; c++) {
        const cell = XLSX.utils.encode_cell({ r: row + i, c });
        if (!ws[cell]) continue;
        ws[cell].s = boldLabelColumns.includes(c) ? filterLabelStyle : filterValueStyle;
      }
    }
    row += 4;

    // ========================
    // TABLE HEADER
    // ========================
    const headers = [
      "#",
      "Vehicle Type",
      "Vehicle Owner",
      "Vehicle Number",
      "Ownership",
      "Lease Type",
      "Fuel Borne By",
      "Hire Rate",
      "Start Date",
      "End Date",
      "Status"
    ];

    const headerRowIndex = row;
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });

    headers.forEach((_, colIndex) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        border: {
          top: thin,
          bottom: thin,
          left: thin,
          right: thin
        }
      };
    });
    row++;

    // TABLE DATA
    sortedData.forEach((reportRow, index) => {
      const hireRateVal = toNumber(reportRow.vehicleHirerate);
      const startSerial = utility.convertDateToExcelSerial(reportRow.startDate);
      const endSerial = utility.convertDateToExcelSerial(reportRow.endDate);

      const rowData = [
        index + 1,
        reportRow.vehicleType || "-",
        reportRow.vehicleOwner || "-",
        reportRow.registrationNo || "-",
        reportRow.ownership || "-",
        reportRow.leaseType || "-",
        reportRow.fuelBorneBy || "-",
        hireRateVal,
        startSerial || "-",
        endSerial || "-",
        reportRow.status || "-"
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });

      // Style data rows and format date columns
      rowData.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        const isNumeric = colIndex === 0 || colIndex === 7;
        const isDate = colIndex === 8 || colIndex === 9;
        ws[cell].s = {
          font: { sz: 10 },
          alignment: { horizontal: isNumeric ? "right" : (isDate ? "center" : "left"), vertical: "center" },
          border: {
            top: thin,
            bottom: thin,
            left: thin,
            right: thin
          }
        };

        if (colIndex === 0) {
          ws[cell].t = "n";
          ws[cell].v = index + 1;
          ws[cell].z = "0";
        }

        // Format date columns (indices 8 and 9 for startDate and endDate)
        if (colIndex === 8 && startSerial) {
          ws[cell].v = startSerial;
          ws[cell].t = "n";
          ws[cell].z = "dd-mm-yyyy";
        }

        if (colIndex === 9 && endSerial) {
          ws[cell].v = endSerial;
          ws[cell].t = "n";
          ws[cell].z = "dd-mm-yyyy";
        }

        if (colIndex === 7) {
          ws[cell].t = "n";
          ws[cell].v = hireRateVal;
          ws[cell].z = "#,##0.00";
        }
      });
      row++;
    });

    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: row - 1, c: headers.length - 1 }
      })
    };

    // Totals Row
    const totalHireRate = sortedData.reduce((sum, item) => sum + toNumber(item.vehicleHirerate), 0);
    const totalsRow = Array(11).fill("");
    totalsRow[6] = "Total:";
    totalsRow[7] = totalHireRate;
    XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: { r: row, c: 0 } });

    for (let c = 0; c < 11; c++) {
      const cell = XLSX.utils.encode_cell({ r: row, c });
      const isNumeric = c === 7;
      ws[cell].s = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: isNumeric || c === 6 ? "right" : "center", vertical: "center" },
        border: { top: thin, bottom: thin, left: thin, right: thin }
      };
      if (isNumeric) {
        ws[cell].t = "n";
        ws[cell].v = totalHireRate;
        ws[cell].z = "#,##0.00";
      }
    }
    row++;

    // Set column widths
    ws["!cols"] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
      { wch: 12 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "VehicleHireReport");
    XLSX.writeFile(wb, "VehicleHireReport.xlsx");
  };

  const exportPdf = async () => {

    const allData = await getAllReportData();

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    let vehicleTypeLabel = "All";
    if (vehicleTypeId) {
      const foundType = vehicleTypes.find(v => String(v.id) === String(vehicleTypeId));
      vehicleTypeLabel = foundType?.vehicleType || (reports[0]?.vehicleType || vehicleTypeId);
    }

    let leaseTypeLabel = "All";
    if (leaseTypeId) {
      const foundType = leaseTypes.find(l => String(l.id) === String(leaseTypeId));
      leaseTypeLabel = foundType?.leaseType || (reports[0]?.leaseType || leaseTypeId);
    }

    let fuelBorneByLabel = "All";
    if (fuelBorneBy) {
      fuelBorneByLabel = fuelBorneBy;
    }

    const doc = new jsPDF("landscape");
    const pdfLogo = await getPdfLogoDataUrl(logo);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const left = 14;
    const right = pageWidth - left;
    const companyName = activeCompany?.companyName || "";
    const title = "Vehicle Hire Report";

    const drawHeader = (pageNumber) => {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);

      doc.setFont(undefined, "bold");
      doc.setFontSize(12);
      const companyNameWidth = doc.getTextWidth(companyName);
      const logoWidth = 5;
      const logoHeight = 10;
      const logoX = left + 6 + (companyNameWidth - logoWidth) / 2;
      const logoY = 8;

      doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
      doc.text(companyName, left + 6, 23);

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, right, 23, { align: "right" });

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text(title, pageWidth / 2, 35, { align: "center" });

      const titleWidth = doc.getTextWidth(title);
      doc.line(pageWidth / 2 - titleWidth / 2, 37, pageWidth / 2 + titleWidth / 2, 37);
    };

    const drawFilterInfo = () => {
      doc.setFontSize(9);
      doc.setFont(undefined, "bold");

      const xLeft = left;
      const xRight = 115;
      const yHeader = 45;

      doc.text("Vehicle Type:", xLeft, yHeader);
      doc.setFont(undefined, "normal");
      doc.text(String(vehicleTypeLabel), xLeft + 30, yHeader);

      doc.setFont(undefined, "bold");
      doc.text("Lease Type:", xLeft, yHeader + 5);
      doc.setFont(undefined, "normal");
      doc.text(String(leaseTypeLabel), xLeft + 30, yHeader + 5);

      doc.setFont(undefined, "bold");
      doc.text("Fuel Borne By:", xLeft, yHeader + 10);
      doc.setFont(undefined, "normal");
      doc.text(String(fuelBorneByLabel), xLeft + 30, yHeader + 10);

      doc.setFont(undefined, "bold");
      doc.text("Start Date:", xRight, yHeader);
      doc.setFont(undefined, "normal");
      doc.text(startDate ? utility.formatDateDMY(startDate) : "-", xRight + 25, yHeader);

      doc.setFont(undefined, "bold");
      doc.text("End Date:", xRight, yHeader + 5);
      doc.setFont(undefined, "normal");
      doc.text(endDate ? utility.formatDateDMY(endDate) : "-", xRight + 25, yHeader + 5);
    };

    const drawPageNumber = (pageNumber, totalPages) => {
      doc.setFont(undefined, "bold");
      doc.setFontSize(8);
      doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 7, { align: "center" });
    };

    const sortedData = [...allData].sort((a, b) => {
      const dateA = new Date(a.startDate || 0).getTime();
      const dateB = new Date(b.startDate || 0).getTime();
      return dateA - dateB;
    });

    const body = sortedData.map((row, index) => [
      index + 1,
      row.vehicleType || "-",
      row.vehicleOwner || "-",
      row.registrationNo || "-",
      row.ownership || "-",
      row.leaseType || "-",
      row.fuelBorneBy || "-",
      row.vehicleHirerate ?? "-",
      utility.formatDateDMY(row.startDate),
      utility.formatDateDMY(row.endDate),
      row.status || "-"
    ]);

    autoTable(doc, {
      startY: 70,
      head: [[
        "S.No",
        "Vehicle Type",
        "Vehicle Owner",
        "Vehicle Number",
        "Ownership",
        "Lease Type",
        "Fuel Borne By",
        "Hire Rate",
        "Start Date",
        "End Date",
        "Status"
      ]],
      body,
      theme: "grid",
      margin: {
        top: 40,
        left: 14,
        right: 14
      },
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0]
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center"
      },
      columnStyles: {
        0: { halign: "right" },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "left" },
        4: { halign: "left" },
        5: { halign: "left" },
        6: { halign: "left" },
        7: { halign: "right" },
        8: { halign: "center" },
        9: { halign: "center" },
        10: { halign: "left" }
      },
      didDrawPage: function (data) {
        drawHeader(data.pageNumber);
        if (data.pageNumber === 1) {
          drawFilterInfo();
        }
      }
    });

    const pdfPageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pdfPageCount; i++) {
      doc.setPage(i);
      drawPageNumber(i, pdfPageCount);
      doc.text("Prepared By", 25, pageHeight - 25);
      doc.text("Checked By", pageWidth / 2, pageHeight - 25, { align: "center" });
      doc.text("Approved By", pageWidth - 25, pageHeight - 25, { align: "right" });
    }

    doc.save("VehicleHireReport.pdf");
  };

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Hire Report" secondTitle="Reports" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />


      <div className="card p-4 bg-white shadow-sm">
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label">Vehicle Type</label>
            <select
              className="form-select"
              value={vehicleTypeId}
              onChange={(e) => setVehicleTypeId(e.target.value)}
            >
              <option value="">All</option>
              {vehicleTypes.map((vehicleType) => (
                <option key={vehicleType.id} value={vehicleType.id}>
                  {vehicleType.vehicleType}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Lease Type</label>
            <select
              className="form-select"
              value={leaseTypeId}
              onChange={(e) => setLeaseTypeId(e.target.value)}
            >
              <option value="">All</option>
              {leaseTypes.map((leaseType) => (
                <option key={leaseType.id} value={leaseType.id}>
                  {leaseType.leaseType}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Fuel Borne By</label>
            <select
              className="form-select"
              value={fuelBorneBy}
              onChange={(e) => setFuelBorneBy(e.target.value)}
            >
              <option value="">All</option>
              <option value="Company">Company</option>
              <option value="Vendor">Vendor</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3 d-flex justify-content-end align-items-center">
         
          <div className="d-flex gap-2">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={() => loadReport(1)} // ✅ RESET TO PAGE 1
              disabled={loading}
            >
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>
        <hr />
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>

            <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>

        
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">

              <tr>
                <th>S.No</th>
                <th>Vehicle Type</th>
                <th>Vehicle Owner</th>
                <th>Vehicle Number</th>
                <th>Ownership</th>
                <th>Lease Type</th>
                <th>Fuel Borne By</th>
                <th>Hire Rate</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
              </tr>

            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="text-center">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((row, index) => (

                  <tr key={row.id || index}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{row.vehicleType || "-"}</td>
                    <td>{row.vehicleOwner || "-"}</td>
                    <td>{row.registrationNo || "-"}</td>
                    <td>{row.ownership || "-"}</td>
                    <td>{row.leaseType || "-"}</td>
                    <td>{row.fuelBorneBy || "-"}</td>
                    <td>{row.vehicleHirerate ?? "-"}</td>
                    <td>{utility.formatDateDDMMMYYYY(row.startDate)}</td>
                    <td>{utility.formatDateDDMMMYYYY(row.endDate)}</td>
                    <td>{row.status || "-"}</td>
                  </tr>

                ))
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">

              {/* TOTAL RECORDS */}
              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>

              {/* PAGINATION BUTTONS */}
              <div className="d-flex gap-2">

                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === 1}
                  onClick={() => loadReport(page - 1)}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm ${page === i + 1 ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => loadReport(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => loadReport(page + 1)}
                >
                  Next
                </button>

              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
